import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasQueryParam, parse } from '../../util/urlHelpers';
import { filterCompletedListings } from '../../util/listingsHelpers';
import {
  getSubcategoriesForCategory,
  normalizeLegacySubcategorySlug,
  STORE_SIDEBAR_CATEGORIES,
} from '../../util/storeTaxonomy';
import { resolveApprovedStoreFallback } from '../../util/api';
import { fetchCreators, fetchStoresLean } from '../../ducks/user.duck';
import { mapTaxonomySearchParamsToSdkQuery } from '../SearchPage/SearchPage.shared';
import { PUBLIC_CATALOG_STOCK_FILTER } from '../../util/publicCatalogStock';
import { isWarehouseChannel, WAREHOUSE_SOURCE_QUERY_VALUE } from '../../util/itemSourceChannel';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/StorePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/StorePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/StorePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/StorePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/StorePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/StorePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/StorePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/StorePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/StorePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/StorePage/QUERY_REVIEWS_ERROR';

export const SET_CATEGORIES = 'app/StorePage/SET_CATEGORIES';
export const SET_SUBCATEGORIES = 'app/StorePage/SET_SUBCATEGORIES';
export const SET_CURRENT_CATEGORY = 'app/StorePage/SET_CURRENT_CATEGORY';
export const SET_CURRENT_SUBCATEGORY = 'app/StorePage/SET_CURRENT_SUBCATEGORY';

export const SET_STORE_FALLBACK_REDIRECT = 'app/StorePage/SET_STORE_FALLBACK_REDIRECT';

export const FETCH_MANUFACTURER_IMAGES_REQUEST = 'app/StorePage/FETCH_MANUFACTURER_IMAGES_REQUEST';
export const FETCH_MANUFACTURER_IMAGES_SUCCESS = 'app/StorePage/FETCH_MANUFACTURER_IMAGES_SUCCESS';
export const FETCH_MANUFACTURER_IMAGES_ERROR = 'app/StorePage/FETCH_MANUFACTURER_IMAGES_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  fetchingListings: true,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  categories: [],
  subcategories: [],
  currentCategory: 'all',
  currentSubcategory: 'all',
  manufacturerImages: {},
  manufacturerImagesInProgress: false,
  manufacturerImagesError: null,
  // Set only when the requested store cannot be shown and an approved store owns the same business
  // domain. StorePage turns this into a `history.replace`. Cleared by SET_INITIAL_STATE, which runs
  // whenever the store id changes, so the substituted store cannot redirect again.
  fallbackStoreId: null,
};

export default function storePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };
    case SET_STORE_FALLBACK_REDIRECT:
      return { ...state, fallbackStoreId: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,
        fetchingListings: true,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, fetchingListings: false, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return {
        ...state,
        fetchingListings: false,
        userListingRefs: [],
        queryListingsError: payload,
      };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    case SET_CATEGORIES:
      return { ...state, categories: payload };
    case SET_SUBCATEGORIES:
      return { ...state, subcategories: payload };
    case SET_CURRENT_CATEGORY:
      return { ...state, currentCategory: payload };
    case SET_CURRENT_SUBCATEGORY:
      return { ...state, currentSubcategory: payload };

    case FETCH_MANUFACTURER_IMAGES_REQUEST:
      return { ...state, manufacturerImagesInProgress: true, manufacturerImagesError: null };
    case FETCH_MANUFACTURER_IMAGES_SUCCESS:
      return {
        ...state,
        manufacturerImagesInProgress: false,
        manufacturerImages: { ...state.manufacturerImages, ...payload },
      };
    case FETCH_MANUFACTURER_IMAGES_ERROR:
      return { ...state, manufacturerImagesInProgress: false, manufacturerImagesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const setStoreFallbackRedirect = storeId => ({
  type: SET_STORE_FALLBACK_REDIRECT,
  payload: storeId,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const setCategories = e => ({
  type: SET_CATEGORIES,
  payload: e,
});

export const setSubcategories = e => ({
  type: SET_SUBCATEGORIES,
  payload: e,
});

export const setCurrentCategory = e => ({
  type: SET_CURRENT_CATEGORY,
  payload: e,
});

export const setCurrentSubcategory = subcategory => ({
  type: SET_CURRENT_SUBCATEGORY,
  payload: subcategory,
});

export const fetchManufacturerImagesRequest = () => ({
  type: FETCH_MANUFACTURER_IMAGES_REQUEST,
});

export const fetchManufacturerImagesSuccess = images => ({
  type: FETCH_MANUFACTURER_IMAGES_SUCCESS,
  payload: images,
});

export const fetchManufacturerImagesError = error => ({
  type: FETCH_MANUFACTURER_IMAGES_ERROR,
  payload: error,
});

// Thunk para obtener imágenes de listings originales de manufacturers
export const fetchManufacturerImages = (listings, config) => async (dispatch, getState, sdk) => {
  // Filtrar listings que son de manufacturers y no tienen imágenes
  const manufacturerListings = listings.filter(listing => {
    const { isFromManufacturer, manufacturerListingId } = listing?.attributes?.publicData || {};
    const hasImages = listing?.images && listing.images.length > 0;
    return isFromManufacturer && manufacturerListingId && !hasImages;
  });

  if (manufacturerListings.length === 0) {
    return;
  }

  dispatch(fetchManufacturerImagesRequest());

  try {
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    // Obtener IDs únicos de listings originales
    const manufacturerListingIds = [
      ...new Set(
        manufacturerListings.map(listing => listing.attributes.publicData.manufacturerListingId)
      ),
    ];

    // Hacer consultas individuales como en ListingPage
    const imagePromises = manufacturerListingIds.map(async listingId => {
      try {
        const response = await sdk.listings.show({
          id: listingId,
          include: ['images'],
          'fields.image': [
            'variants.scaled-small',
            'variants.scaled-medium',
            'variants.scaled-large',
            'variants.scaled-xlarge',
            `variants.${variantPrefix}`,
            `variants.${variantPrefix}-2x`,
            `variants.${variantPrefix}-4x`,
            `variants.${variantPrefix}-6x`,
            'variants.facebook',
            'variants.twitter',
            'variants.square-small',
            'variants.square-small2x',
          ],
          ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
          ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
        });

        return { listingId, response };
      } catch (error) {
        console.error(`Error fetching listing ${listingId}:`, error);
        return { listingId, response: null };
      }
    });

    const results = await Promise.all(imagePromises);

    // Agregar las entidades al store y crear el mapa de imágenes
    const imagesMap = {};
    results.forEach(({ listingId, response }) => {
      if (response) {
        dispatch(addMarketplaceEntities(response));

        // Denormalizar las imágenes como en ListingPage
        const listing = response?.data?.data;
        const imageRefs = listing?.relationships?.images?.data || [];
        const images = getMarketplaceEntities(getState(), imageRefs);

        if (images.length > 0) {
          imagesMap[listingId] = images;
        }
      }
    });

    dispatch(fetchManufacturerImagesSuccess(imagesMap));
    return imagesMap;
  } catch (error) {
    dispatch(fetchManufacturerImagesError(storableError(error)));
    console.error('Error fetching manufacturer images:', error);
  }
};

const fetchAllListingPages = (sdk, queryParams) => {
  return sdk.listings.query(queryParams).then(response => {
    const totalPages = response?.data?.meta?.totalPages || 1;

    if (totalPages <= 1) {
      return response;
    }

    const remainingPages = [];
    for (let page = 2; page <= totalPages; page++) {
      remainingPages.push(sdk.listings.query({ ...queryParams, page }));
    }

    return Promise.all(remainingPages).then(restResponses => {
      let allData = [...(response?.data?.data || [])];
      let allIncluded = [...(response?.data?.included || [])];

      restResponses.forEach(resp => {
        if (resp?.data?.data) {
          allData = allData.concat(resp.data.data);
          allIncluded = allIncluded.concat(resp.data.included || []);
        }
      });

      response.data.data = allData;
      response.data.included = allIncluded;
      return response;
    });
  });
};

// ================ Thunks ================ //

export const queryUserListings = (userId, config, search = '', keywords = null) => (
  dispatch,
  getState,
  sdk
) => {
  let state = getState();
  let { domLoaded = false } = state?.ui || {};

  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // const variantPrefix = 'square-small';
  let keywordsMaybe = keywords ? { keywords } : {};

  let queryParams = {
    ...keywordsMaybe,
    page: 1,
    perPage: 100,
    include: ['author', 'author.profileImage', 'images'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.profileImage',
      'profile.publicData',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    ...PUBLIC_CATALOG_STOCK_FILTER,
  };

  const filter = hasQueryParam({ search }, 'pub_category', true);
  const filterSubcategory = hasQueryParam({ search }, 'pub_subcategory', true);

  dispatch(setCategories(STORE_SIDEBAR_CATEGORIES));

  let currentCategory = 'all';
  if (filter && filter !== 'all') {
    queryParams['pub_category'] = filter;
    currentCategory = filter;
  }
  dispatch(setCurrentCategory(currentCategory));

  // Seller-scoped iframe/store filters should mirror the taxonomy helper for the
  // active category instead of hosted listingFields enums.
  const filterSubcategories = getSubcategoriesForCategory(currentCategory);
  dispatch(setSubcategories(filterSubcategories));

  let currentSubcategory = 'all';
  if (filterSubcategory) {
    const normalizedSubcategory = normalizeLegacySubcategorySlug(filterSubcategory);
    let inOptions = filterSubcategories.find(fs => fs.option === normalizedSubcategory);
    if (inOptions) {
      if (normalizedSubcategory !== 'all') {
        queryParams['pub_subcategory'] = normalizedSubcategory;
      }
      currentSubcategory = normalizedSubcategory;
    }
  }
  dispatch(setCurrentSubcategory(currentSubcategory));

  let shouldBypassSellerScope = false;

  // Parse all additional pub_* filter params from URL search string
  if (search) {
    let searchStr = search;
    if (searchStr.startsWith('?')) searchStr = searchStr.slice(1);
    const urlParams = new URLSearchParams(searchStr);
    const channelValue = urlParams.get('channel');
    shouldBypassSellerScope = isWarehouseChannel(channelValue);

    if (!shouldBypassSellerScope) {
      queryParams.author_id = userId;
    }

    if (isWarehouseChannel(channelValue)) {
      queryParams.pub_source = WAREHOUSE_SOURCE_QUERY_VALUE;
    }

    urlParams.forEach((value, key) => {
      if (
        key.startsWith('pub_') &&
        key !== 'pub_category' &&
        key !== 'pub_subcategory' &&
        key !== 'pub_brand' &&
        value
      ) {
        queryParams[key] = value;
      }
    });

    // Handle sort param
    const sortValue = urlParams.get('sort');
    if (sortValue) {
      queryParams.sort = sortValue;
    }
  }

  if (!shouldBypassSellerScope && !queryParams.author_id) {
    queryParams.author_id = userId;
  }

  const mappedQueryParams = mapTaxonomySearchParamsToSdkQuery(queryParams);

  return fetchAllListingPages(sdk, mappedQueryParams)
    .then(response => {
      const responseListings = filterCompletedListings(response?.data?.data || []);
      const creatorsId = (response?.data?.data || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);
      if (domLoaded) {
        dispatch(fetchCreators(creatorsId));
        dispatch(fetchStoresLean());
      }
      // Pick only the id and type properties from the response listings
      response.data.data = responseListings;
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));

      // Obtener imágenes de manufacturers si es necesario
      if (response.data.data.length > 0) {
        dispatch(fetchManufacturerImages(response.data.data, config));
      }

      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const showUser = userId => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      // include: ['profileImage'],
      // 'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const reloadListings = (params, keywords, config, search = '') => dispatch => {
  const userId = new UUID(params.id);
  dispatch(queryUserListings(userId, config, search, keywords));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const parsedSearch = parse(search || '');
  const initialKeywords = parsedSearch.keywords || null;
  const currentStoreUserId = getState()?.StorePage?.userId?.uuid;
  const shouldResetStoreState = currentStoreUserId !== params.id;

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  if (shouldResetStoreState) {
    dispatch(setInitialState());
  }

  return Promise.all([
    dispatch(showUser(userId)),
    dispatch(queryUserListings(userId, config, search, initialKeywords)),
  ]).then(([userResponse]) => {
    // Check if the seller is approved
    const user = userResponse?.data?.data;
    const publicData = user?.attributes?.profile?.publicData || {};
    const isApprovedSeller = publicData.approved_seller === true;

    // If the user is a seller (provider) but not approved, block access to their store page
    const userType = publicData.userType;
    const isSeller = userType === 'provider' || userType === 'provider-customer';

    if (!isSeller || isApprovedSeller) {
      return userResponse;
    }

    // Create a 404-like error to show NotFoundPage
    const rejectRequestedStore = () => {
      const error = new Error('Seller not approved');
      error.status = 404;
      error.statusText = 'Not Found';
      dispatch(showUserError(storableError(error)));
      throw error;
    };

    // The requested store cannot be shown. Before giving up, check whether an approved store owns
    // the same business domain: dealer embeds and shared links outlive the account they were built
    // from, so a stale or non-owner id should reach the real store instead of a dead page.
    //
    // The lookup needs this template's own API, which is browser-only (`apiBaseUrl()` reads
    // `window.location.origin`). During SSR the request is rejected exactly as before, and the
    // client rerun of `loadData` performs the lookup.
    if (typeof window === 'undefined') {
      return rejectRequestedStore();
    }

    return resolveApprovedStoreFallback({ userId: params.id })
      .then(({ dealerId }) => {
        if (!dealerId || dealerId === params.id) {
          return rejectRequestedStore();
        }

        dispatch(setStoreFallbackRedirect(dealerId));
        return userResponse;
      })
      .catch(e => {
        // Never let a failing lookup swallow the outcome: without a substitute this page is still
        // a 404, and rethrowing the rejection above would skip `showUserError`.
        if (e?.status === 404) {
          throw e;
        }
        return rejectRequestedStore();
      });
  });
};

// ================ Selectors ================ //

export const selectStorePage = state => state.StorePage;

export const selectCurrentUser = state => state.user.currentUser;
export const selectUser = state => {
  const { userId } = state.StorePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  return userMatches.length === 1 ? userMatches[0] : null;
};

export const selectListings = state => {
  const { userListingRefs } = state.StorePage;
  return getMarketplaceEntities(state, userListingRefs);
};

export const selectManufacturerImages = state => state.StorePage.manufacturerImages;
export const selectManufacturerImagesInProgress = state =>
  state.StorePage.manufacturerImagesInProgress;
export const selectManufacturerImagesError = state => state.StorePage.manufacturerImagesError;
export const selectFallbackStoreId = state => state.StorePage.fallbackStoreId;
